import type { ConsumerApiClient } from '../client';

export type RecipientStatus = 'active' | 'released' | 'transferred' | 'deceased' | 'not_found';

/* ── Flat contact shape expected by all screens ── */
export interface Contact {
  id: string;
  recipientId: string;
  firstName: string;
  lastName: string;
  inmateNumber: string;
  facilityId: string;
  facilityName: string;
  facilityState: string;
  facilityCity: string;
  facilityAddress: string;
  status: RecipientStatus;
  createdAt: string;
}

/* ── Shape the API actually returns (nested relations) ── */
interface ContactRecipient {
  id: string;
  firstName: string;
  lastName: string;
  inmateNumber: string;
  status?: string;
}

interface ContactFacility {
  id: string;
  name: string;
  state: string;
  city?: string;
  mailingAddress?: Record<string, any> | null;
}

interface ContactApiResponse {
  id: string;
  recipientId: string;
  facilityId: string;
  createdAt: string;
  recipient?: ContactRecipient;
  facility?: ContactFacility;
  // The flat fields may also be present in some contexts (e.g. addContact)
  firstName?: string;
  lastName?: string;
  inmateNumber?: string;
  facilityName?: string;
  facilityState?: string;
  status?: string;
}

/**
 * Build a one-line mailing address from the jsonb mailingAddress column.
 * Typical shape: { street1, street2, city, state, zip }.
 */
function formatMailingAddress(addr?: Record<string, any> | null): string {
  if (!addr) return '';
  const parts: string[] = [];
  if (addr.street || addr.street1) parts.push(addr.street || addr.street1);
  if (addr.street2) parts.push(addr.street2);
  const cityStateZip = [
    addr.city,
    addr.state ? `${addr.state}` : undefined,
    addr.zip || addr.zipCode || addr.postalCode,
  ]
    .filter(Boolean)
    .join(', ');
  if (cityStateZip) parts.push(cityStateZip);
  return parts.join(', ');
}

/**
 * Normalise a raw API contact into the flat Contact shape.
 * Handles both nested (recipient/facility) and already-flat payloads.
 */
function normaliseContact(raw: ContactApiResponse): Contact {
  return {
    id: raw.id,
    recipientId: raw.recipientId,
    facilityId: raw.facilityId,
    createdAt: raw.createdAt,
    firstName: raw.recipient?.firstName ?? raw.firstName ?? '',
    lastName: raw.recipient?.lastName ?? raw.lastName ?? '',
    inmateNumber: raw.recipient?.inmateNumber ?? raw.inmateNumber ?? '',
    facilityName: raw.facility?.name ?? raw.facilityName ?? '',
    facilityState: raw.facility?.state ?? raw.facilityState ?? '',
    facilityCity: raw.facility?.city ?? '',
    facilityAddress: formatMailingAddress(raw.facility?.mailingAddress),
    status: (raw.recipient?.status ?? raw.status ?? 'active') as RecipientStatus,
  };
}

export interface CreateContactRequest {
  firstName: string;
  lastName: string;
  inmateNumber: string;
  facilityId: string;
}

export interface ContactAttributeValue {
  id: string;
  facilityAttributeId: string;
  attributeLabel: string;
  attributeName: string;
  value: string;
  isRequired: boolean;
  clientAssistUrl?: string | null;
}

export interface UpsertContactAttributesRequest {
  attributes: Array<{ facilityAttributeId: string; value: string }>;
}

export interface ContactsApi {
  getContacts(): Promise<Contact[]>;
  getContact(id: string): Promise<Contact>;
  createContact(data: CreateContactRequest): Promise<Contact>;
  updateContact(
    id: string,
    data: Partial<CreateContactRequest & { nickname?: string }>,
  ): Promise<Contact>;
  deleteContact(id: string): Promise<void>;
  getContactAttributes(contactId: string): Promise<ContactAttributeValue[]>;
  upsertContactAttributes(
    contactId: string,
    data: UpsertContactAttributesRequest,
  ): Promise<ContactAttributeValue[]>;
}

export function createContactsApi(client: ConsumerApiClient): ContactsApi {
  return {
    async getContacts() {
      const raw = await client.get<ContactApiResponse[]>('/contacts');
      return raw.map(normaliseContact);
    },

    async getContact(id) {
      const raw = await client.get<ContactApiResponse>(`/contacts/${id}`);
      return normaliseContact(raw);
    },

    async createContact(data) {
      const raw = await client.post<ContactApiResponse>('/contacts', data);
      return normaliseContact(raw);
    },

    async updateContact(id, data) {
      const raw = await client.put<ContactApiResponse>(`/contacts/${id}`, data);
      return normaliseContact(raw);
    },

    deleteContact(id) {
      return client.delete(`/contacts/${id}`);
    },

    getContactAttributes(contactId) {
      return client.get(`/contacts/${contactId}/attributes`);
    },

    upsertContactAttributes(contactId, data) {
      return client.put(`/contacts/${contactId}/attributes`, data);
    },
  };
}
